import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  getConversionSummary,
  getLeads,
  updateLeadStatus,
  type ConversionSummary,
  type LeadRow,
} from "@/lib/crm.functions";

export const Route = createFileRoute("/admin/conversions/")({
  head: () => ({
    meta: [
      { title: "Conversions & Leads — Optimize Plus" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  beforeLoad: async () => {
    const { checkAdminUnlocked } = await import("@/lib/admin-gate.functions");
    await checkAdminUnlocked();
  },
  component: ConversionsDashboard,
});

const RANGES = [7, 30, 90] as const;
type Range = (typeof RANGES)[number];

const STATUSES = ["new", "contacted", "qualified", "won", "lost", "spam"] as const;

function ConversionsDashboard() {
  const [days, setDays] = useState<Range>(30);
  const summaryFn = useServerFn(getConversionSummary);
  const leadsFn = useServerFn(getLeads);
  const statusFn = useServerFn(updateLeadStatus);
  const qc = useQueryClient();

  const summary = useQuery<ConversionSummary>({
    queryKey: ["conversion-summary", days],
    queryFn: () => summaryFn({ data: { days } }),
  });
  const leads = useQuery<LeadRow[]>({
    queryKey: ["leads", days],
    queryFn: () => leadsFn({ data: { days } }),
  });

  const s = summary.data;

  return (
    <main className="container-page py-12 space-y-8">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl">Conversions &amp; Leads</h1>
          <p className="text-sm text-muted-foreground mt-1">
            First-party goal tracking for form submissions, CTA clicks, and call/email taps.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {RANGES.map((r) => (
            <Button
              key={r}
              variant={r === days ? "default" : "outline"}
              size="sm"
              onClick={() => setDays(r)}
            >
              {r}d
            </Button>
          ))}
          <Button asChild variant="secondary" size="sm">
            <a href={`/admin/export.csv?type=leads&days=${days}`} download>
              Leads CSV
            </a>
          </Button>
          <Button asChild variant="secondary" size="sm">
            <a href={`/admin/export.csv?type=conversions&days=${days}`} download>
              Events CSV
            </a>
          </Button>
          <Button asChild variant="outline" size="sm">
            <a href="/admin/webhooks/">Webhooks</a>
          </Button>
        </div>

      </header>

      <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {[
          { label: "Leads submitted", value: s?.leadCount ?? 0 },
          { label: "CTA clicks", value: s?.ctaCount ?? 0 },
          { label: "Lead / CTA rate", value: `${s?.conversionRatePct ?? 0}%` },
          {
            label: "Call + email taps",
            value:
              (s?.totals.find((t) => t.event === "call_click")?.count ?? 0) +
              (s?.totals.find((t) => t.event === "email_click")?.count ?? 0),
          },
        ].map((kpi) => (
          <Card key={kpi.label}>
            <CardHeader className="pb-2">
              <CardTitle className="text-xs uppercase tracking-widest text-muted-foreground">
                {kpi.label}
              </CardTitle>
            </CardHeader>
            <CardContent className="text-3xl font-display">{kpi.value}</CardContent>
          </Card>
        ))}
      </section>

      <Card>
        <CardHeader>
          <CardTitle>Daily goal completions</CardTitle>
        </CardHeader>
        <CardContent className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={s?.daily ?? []}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.15} />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
              <Tooltip />
              <Legend />
              <Bar dataKey="leads" name="Leads" fill="hsl(var(--primary))" />
              <Bar dataKey="ctas" name="CTA clicks" fill="hsl(var(--muted-foreground))" />
              <Bar dataKey="calls" name="Call/email" fill="hsl(var(--accent))" />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Top converting pages</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Path</TableHead>
                  <TableHead className="text-right">Events</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(s?.topPaths ?? []).map((p) => (
                  <TableRow key={p.path}>
                    <TableCell className="font-mono text-xs">{p.path}</TableCell>
                    <TableCell className="text-right">{p.count}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Top CTAs</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Label</TableHead>
                  <TableHead className="text-right">Clicks</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(s?.topLabels ?? []).map((l) => (
                  <TableRow key={l.label}>
                    <TableCell className="text-xs">{l.label}</TableCell>
                    <TableCell className="text-right">{l.count}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Leads ({leads.data?.length ?? 0})</CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Received</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Company</TableHead>
                <TableHead>Source</TableHead>
                <TableHead>Message</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(leads.data ?? []).map((l) => (
                <TableRow key={l.id}>
                  <TableCell className="whitespace-nowrap text-xs">
                    {new Date(l.created_at).toLocaleString()}
                  </TableCell>
                  <TableCell>{l.name}</TableCell>
                  <TableCell>
                    <a className="underline" href={`mailto:${l.email}`}>
                      {l.email}
                    </a>
                  </TableCell>
                  <TableCell>{l.company ?? "—"}</TableCell>
                  <TableCell className="text-xs font-mono">
                    {l.service_slug || l.location_slug || l.source_path || "—"}
                  </TableCell>
                  <TableCell className="max-w-md text-xs">{l.message}</TableCell>
                  <TableCell>
                    <select
                      className="rounded-md border border-border bg-background px-2 py-1 text-xs"
                      value={l.status}
                      aria-label={`Status for ${l.name}`}
                      onChange={async (e) => {
                        await statusFn({
                          data: { id: l.id, status: e.target.value as (typeof STATUSES)[number] },
                        });
                        void qc.invalidateQueries({ queryKey: ["leads", days] });
                      }}
                    >
                      {STATUSES.map((st) => (
                        <option key={st} value={st}>
                          {st}
                        </option>
                      ))}
                    </select>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </main>
  );
}
